import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import InventoryContainer from './container';
import useDebounce from 'hooks/useDebounce';
import {
  useGetInventoryCategoriesQuery,
  useAddInventoryCategoryMutation,
  useUpdateInventoryCategoryMutation,
  useDeleteInventoryCategoryMutation,
  useChangeInventoryCategoryStatusMutation,
  useGetInventoryItemsQuery,
  useAddInventoryItemMutation,
  useUpdateInventoryItemMutation,
  useDeleteInventoryItemMutation,
  useChangeInventoryItemStatusMutation,
  useGetRecipesQuery,
  useAddUpdateRecipeMutation,
  useDeleteRecipeMutation,
  useGetWastagesQuery,
  useAddWastageMutation,
  useUpdateWastageMutation,
  useDeleteWastageMutation,
  useGetStockAdjustmentsQuery,
  useAddStockAdjustmentMutation,
  useUpdateStockAdjustmentMutation,
  useDeleteStockAdjustmentMutation,
  useGetInventoryDashboardQuery,
} from './services';
import { useGetListFoodItemsQuery } from 'pages/foodItems/services';
import InventoryCategoriesModal from 'components/Modal/InventoryCategories/InventoryCategoriesModal';
import InventoryItemsModal from 'components/Modal/InventoryItems/InventoryItemsModal';
import RecipesModal from 'components/Modal/Recipes/RecipesModal';
import ViewIngredientsModal from 'components/Modal/Recipes/ViewIngredientsModal';
import RecordWastageModal from 'components/Modal/Wastage/RecordWastageModal';
import StockAdjustmentModal from 'components/Modal/StockAdjustment/StockAdjustmentModal';
import ConfirmationDialog from 'components/Modal/ConfirmationDialog/ConfirmationDialog';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import RecipeForm from './RecipeForm';
import MainContainer from 'theme/MainConatiner';

const Inventory = () => {
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Modals state
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [recipeFormOpen, setRecipeFormOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [viewRecipe, setViewRecipe] = useState(null);
  const [viewIngredientsOpen, setViewIngredientsOpen] = useState(false);

  const [wastageModalOpen, setWastageModalOpen] = useState(false);
  const [editingWastage, setEditingWastage] = useState(null);

  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState(null);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deletingEntity, setDeletingEntity] = useState(null); // { type: 'categories' | 'items' | 'recipes', data: object }

  // Status toggling tracking state
  const [categoryTogglingId, setCategoryTogglingId] = useState(null);
  const [itemTogglingId, setItemTogglingId] = useState(null);

  // Handler to switch tabs and refetch data for that tab on click
  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);

    if (newTab === 'dashboard') {
      refetchDashboard?.();
    } else if (newTab === 'categories') {
      refetchCategories?.();
    } else if (newTab === 'items') {
      refetchItems?.();
    } else if (newTab === 'recipes') {
      refetchRecipes?.();
    } else if (newTab === 'wastage') {
      refetchWastages?.();
    } else if (newTab === 'adjustments') {
      refetchAdjustments?.();
    }
  };

  // Reset pagination/filters when switching tabs
  useEffect(() => {
    setSearchQuery('');
    setPaginationModel({ page: 0, pageSize: 10 });
    setSelectedCategoryFilter('');
    setSelectedStatusFilter('all');
  }, [selectedTab]);

  // Queries
  const [dashboardDateRange, setDashboardDateRange] = useState({
    startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
    key: 'selection',
    color: '#000'
  });

  const {
    data: dashboardResp,
    isFetching: dashboardLoading,
    refetch: refetchDashboard
  } = useGetInventoryDashboardQuery({
    startDate: dashboardDateRange.startDate ? format(dashboardDateRange.startDate, 'yyyy-MM-dd') : '',
    endDate: dashboardDateRange.endDate ? format(dashboardDateRange.endDate, 'yyyy-MM-dd') : '',
  });
  const dashboardData = dashboardResp?.data || {};

  const {
    data: categoriesResp,
    isLoading: categoriesLoading,
    isFetching: categoriesFetching,
    refetch: refetchCategories
  } = useGetInventoryCategoriesQuery(
    selectedTab === 'categories' && searchQuery ? debouncedSearchQuery : undefined
  );
  const categories = categoriesResp?.data?.records || [];

  const {
    data: itemsResp,
    isLoading: itemsLoading,
    isFetching: itemsFetching,
    refetch: refetchItems
  } = useGetInventoryItemsQuery({
    search: selectedTab === 'items' && searchQuery ? debouncedSearchQuery : undefined,
    category_id: selectedCategoryFilter || undefined,
    status: selectedTab === 'items' && selectedStatusFilter !== 'all' ? selectedStatusFilter : undefined,
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
  });
  const items = itemsResp?.data?.records || [];
  const itemsCount = itemsResp?.data?.totalCount || 0;

  const {
    data: recipesResp,
    isLoading: recipesLoading,
    isFetching: recipesFetching,
    refetch: refetchRecipes
  } = useGetRecipesQuery();
  const recipesData = recipesResp?.data || [];
  const filteredRecipes = recipesData.filter(rec =>
    rec.name?.toLowerCase().includes((selectedTab === 'recipes' && searchQuery ? debouncedSearchQuery : '').toLowerCase())
  );
  const recipesCount = filteredRecipes.length;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const paginatedRecipes = filteredRecipes.slice(startIndex, startIndex + paginationModel.pageSize);

  const {
    data: wastagesResp,
    isLoading: wastagesLoading,
    isFetching: wastagesFetching,
    refetch: refetchWastages
  } = useGetWastagesQuery({
    search: selectedTab === 'wastage' && searchQuery ? debouncedSearchQuery : undefined,
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
  });
  const wastage = wastagesResp?.data?.records || [];
  const wastageCount = wastagesResp?.data?.totalCount || 0;

  const {
    data: adjustmentsResp,
    isLoading: adjustmentsLoading,
    isFetching: adjustmentsFetching,
    refetch: refetchAdjustments
  } = useGetStockAdjustmentsQuery({
    search: selectedTab === 'adjustments' && searchQuery ? debouncedSearchQuery : undefined,
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
  });
  const adjustments = adjustmentsResp?.data?.records || [];
  const adjustmentsCount = adjustmentsResp?.data?.totalCount || 0;

  // Fetch all inventory items (unpaginated) for ingredient selection dropdowns
  const { data: allInventoryItemsResp } = useGetInventoryItemsQuery({ perPage: 'all' });
  const allInventoryItems = allInventoryItemsResp?.data?.records || [];

  const { data: foodItemsResp } = useGetListFoodItemsQuery();
  const foodItems = foodItemsResp?.data?.records || [];

  // Mutations
  const [addCategory, { isLoading: addCategoryLoading }] = useAddInventoryCategoryMutation();
  const [updateCategory, { isLoading: updateCategoryLoading }] = useUpdateInventoryCategoryMutation();
  const [deleteCategory, { isLoading: deleteCategoryLoading }] = useDeleteInventoryCategoryMutation();
  const [changeCategoryStatus] = useChangeInventoryCategoryStatusMutation();

  const [addItem, { isLoading: addItemLoading }] = useAddInventoryItemMutation();
  const [updateItem, { isLoading: updateItemLoading }] = useUpdateInventoryItemMutation();
  const [deleteItem, { isLoading: deleteItemLoading }] = useDeleteInventoryItemMutation();
  const [changeItemStatus] = useChangeInventoryItemStatusMutation();

  const [addUpdateRecipe, { isLoading: recipeSaving }] = useAddUpdateRecipeMutation();
  const [deleteRecipe, { isLoading: recipeDeleting }] = useDeleteRecipeMutation();

  const [addWastage, { isLoading: addWastageLoading }] = useAddWastageMutation();
  const [updateWastage, { isLoading: updateWastageLoading }] = useUpdateWastageMutation();
  const [deleteWastage, { isLoading: deleteWastageLoading }] = useDeleteWastageMutation();

  const [addAdjustment, { isLoading: addAdjustmentLoading }] = useAddStockAdjustmentMutation();
  const [updateAdjustment, { isLoading: updateAdjustmentLoading }] = useUpdateStockAdjustmentMutation();
  const [deleteAdjustment, { isLoading: deleteAdjustmentLoading }] = useDeleteStockAdjustmentMutation();

  // Handlers
  const handleAddBtn = () => {
    if (selectedTab === 'categories') {
      setEditingCategory(null);
      setCategoryModalOpen(true);
    } else if (selectedTab === 'items') {
      setEditingItem(null);
      setItemModalOpen(true);
    } else if (selectedTab === 'recipes') {
      setEditingRecipe(null);
      setRecipeFormOpen(true);
    } else if (selectedTab === 'wastage') {
      setEditingWastage(null);
      setWastageModalOpen(true);
    } else if (selectedTab === 'adjustments') {
      setEditingAdjustment(null);
      setAdjustmentModalOpen(true);
    }
  };

  const handleEdit = (data) => {
    if (selectedTab === 'categories') {
      setEditingCategory(data);
      setCategoryModalOpen(true);
    } else if (selectedTab === 'items') {
      setEditingItem(data);
      setItemModalOpen(true);
    } else if (selectedTab === 'recipes') {
      setEditingRecipe(data);
      setRecipeFormOpen(true);
    } else if (selectedTab === 'wastage') {
      setEditingWastage(data);
      setWastageModalOpen(true);
    } else if (selectedTab === 'adjustments') {
      setEditingAdjustment(data);
      setAdjustmentModalOpen(true);
    }
  };

  const handleQuickAdjustment = (item) => {
    setEditingAdjustment({ inventory_item_id: item.id });
    setAdjustmentModalOpen(true);
  };

  const handleViewIngredients = (recipe) => {
    setViewRecipe(recipe);
    setViewIngredientsOpen(true);
  };

  const handleDelete = (data) => {
    setDeletingEntity({ type: selectedTab, data });
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEntity) return;
    const { type, data } = deletingEntity;
    try {
      if (type === 'categories') {
        await deleteCategory(data.id).unwrap();
      } else if (type === 'items') {
        await deleteItem(data.id).unwrap();
      } else if (type === 'recipes') {
        await deleteRecipe(data.id).unwrap();
      } else if (type === 'wastage') {
        await deleteWastage(data.id).unwrap();
      } else if (type === 'adjustments') {
        await deleteAdjustment(data.id).unwrap();
      }
      setConfirmDialogOpen(false);
      setDeletingEntity(null);
    } catch (error) {
      toast.error(error?.message || `Failed to delete ${type}`);
    }
  };

  const handleToggleStatus = async (row) => {
    if (selectedTab === 'categories') {
      setCategoryTogglingId(row.id);
      try {
        await changeCategoryStatus({
          id: row.id,
          body: !row.active_status
        }).unwrap();
      } catch (error) {
        toast.error(error?.message || 'Failed to update category status');
      } finally {
        setCategoryTogglingId(null);
      }
    } else if (selectedTab === 'items') {
      setItemTogglingId(row.id);
      try {
        await changeItemStatus({
          id: row.id,
          body: !row.active_status
        }).unwrap();
      } catch (error) {
        toast.error(error?.message || 'Failed to update item status');
      } finally {
        setItemTogglingId(null);
      }
    }
  };

  // Submit handlers
  const handleCategorySubmit = async (values) => {
    try {
      if (editingCategory) {
        await updateCategory({ id: editingCategory.id, ...values }).unwrap();
      } else {
        await addCategory(values).unwrap();
      }
      setCategoryModalOpen(false);
      setEditingCategory(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to save category');
    }
  };

  const handleItemSubmit = async (values) => {
    try {
      if (editingItem) {
        await updateItem({ id: editingItem.id, ...values }).unwrap();
      } else {
        await addItem(values).unwrap();
      }
      setItemModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to save item');
    }
  };

  const handleRecipeSubmit = async (values) => {
    try {
      await addUpdateRecipe(values).unwrap();
      setRecipeModalOpen(false);
      setRecipeFormOpen(false);
      setEditingRecipe(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to save recipe');
    }
  };

  const handleWastageSubmit = async (values) => {
    try { 
      if (editingWastage && editingWastage.id) {
        await updateWastage({ id: editingWastage.id, ...values }).unwrap();
      } else {
        await addWastage(values).unwrap();
      }
      setWastageModalOpen(false);
      setEditingWastage(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to record wastage');
    }
  };

  const handleAdjustmentSubmit = async (values) => {
    try {
      if (editingAdjustment && editingAdjustment.id) {
        await updateAdjustment({ id: editingAdjustment.id, ...values }).unwrap();
      } else {
        await addAdjustment(values).unwrap();
      }
      setAdjustmentModalOpen(false);
      setEditingAdjustment(null);
    } catch (error) {
      toast.error(error?.message || 'Failed to save stock adjustment');
    }
  };

  if (selectedTab === 'recipes' && recipeFormOpen) {
    return (
      <MainContainer padding="30px">
        <RecipeForm
          onClose={() => { setRecipeFormOpen(false); setEditingRecipe(null); }}
          initialData={editingRecipe}
          onSubmit={handleRecipeSubmit}
          loading={recipeSaving}
          foodItems={foodItems}
          inventoryItems={allInventoryItems}
          recipes={recipesData}
        />
      </MainContainer>
    );
  }

  return (
    <>
      <InventoryContainer
        selectedTab={selectedTab}
        setSelectedTab={handleTabChange}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        debouncedSearchQuery={debouncedSearchQuery}
        categories={categories}
        categoriesLoading={categoriesLoading}
        categoryTogglingId={categoryTogglingId}
        items={items}
        itemsLoading={itemsLoading}
        itemsCount={itemsCount}
        itemTogglingId={itemTogglingId}
        recipes={paginatedRecipes}
        recipesLoading={recipesLoading}
        recipesCount={recipesCount}
        wastage={wastage}
        wastagesLoading={wastagesLoading}
        wastageCount={wastageCount}
        adjustments={adjustments}
        adjustmentsLoading={adjustmentsLoading}
        adjustmentsCount={adjustmentsCount}
        handleAddBtn={handleAddBtn}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleToggleStatus={handleToggleStatus}
        handleViewIngredients={handleViewIngredients}
        handleQuickAdjustment={handleQuickAdjustment}
        selectedCategoryFilter={selectedCategoryFilter}
        setSelectedCategoryFilter={setSelectedCategoryFilter}
        selectedStatusFilter={selectedStatusFilter}
        setSelectedStatusFilter={setSelectedStatusFilter}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        dashboardDateRange={dashboardDateRange}
        setDashboardDateRange={setDashboardDateRange}
        dashboardData={dashboardData}
        dashboardLoading={dashboardLoading}
      />

      <InventoryCategoriesModal
        open={categoryModalOpen}
        onClose={() => { setCategoryModalOpen(false); setEditingCategory(null); }}
        initialData={editingCategory}
        onSubmit={handleCategorySubmit}
        loading={addCategoryLoading || updateCategoryLoading}
      />

      <InventoryItemsModal
        open={itemModalOpen}
        onClose={() => { setItemModalOpen(false); setEditingItem(null); }}
        initialData={editingItem}
        onSubmit={handleItemSubmit}
        loading={addItemLoading || updateItemLoading}
        categories={categories}
      />

      <RecipesModal
        open={recipeModalOpen}
        onClose={() => { setRecipeModalOpen(false); setEditingRecipe(null); }}
        initialData={editingRecipe}
        onSubmit={handleRecipeSubmit}
        loading={recipeSaving}
        foodItems={foodItems}
        inventoryItems={allInventoryItems}
        recipes={recipesData}
      />

      <ViewIngredientsModal
        open={viewIngredientsOpen}
        onClose={() => { setViewIngredientsOpen(false); setViewRecipe(null); }}
        recipe={viewRecipe}
      />

      <RecordWastageModal
        open={wastageModalOpen}
        onClose={() => { setWastageModalOpen(false); setEditingWastage(null); }}
        onSubmit={handleWastageSubmit}
        loading={addWastageLoading || updateWastageLoading}
        inventoryItems={allInventoryItems}
        initialData={editingWastage}
      />

      <StockAdjustmentModal
        open={adjustmentModalOpen}
        onClose={() => { setAdjustmentModalOpen(false); setEditingAdjustment(null); }}
        onSubmit={handleAdjustmentSubmit}
        loading={addAdjustmentLoading || updateAdjustmentLoading}
        inventoryItems={allInventoryItems}
        initialData={editingAdjustment}
      />

      <ConfirmationDialog
        open={confirmDialogOpen}
        handleClose={() => { setConfirmDialogOpen(false); setDeletingEntity(null); }}
        handleConfirm={handleConfirmDelete}
        isLoading={deleteCategoryLoading || deleteItemLoading || recipeDeleting || deleteWastageLoading || deleteAdjustmentLoading}
        btnText="Delete"
        action="Delete"
        infoText={`Are you sure you want to delete this ${deletingEntity?.type === 'categories' ? 'category' : deletingEntity?.type === 'items' ? 'item' : deletingEntity?.type === 'recipes' ? 'recipe' : deletingEntity?.type === 'wastage' ? 'wastage record' : 'stock adjustment'}? This action cannot be undone.`}
      />
    </>
  );
};

export default Inventory;
